'use client';

import { useConfig, useGate } from 'statsig-react';

import useDisplayAffirm from '@/app/hooks/useDisplayAffirm';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';
import useMatchUserCountry from '@/app/hooks/useMatchUserCountry';
import { useSikaPayments } from '@/app/hooks/useSikaPayments';

import type { GenericEyebrowProps } from '../EyebrowMessage';
import {
  AffiliateMessage,
  AffirmMessage,
  AmazonMessage,
  BestBuyMessage,
  ExpeditedShippingMessage,
  FSAMessage,
  GenericEyebrow,
  JapanMessage,
  NaturalCyclesMessage,
} from '../EyebrowMessage';
import type { EyebrowProps } from '../types';

const DEFAULT_CLASS_NAME = 'bg-sandstone-200 text-sandstone-500';

export const EYEBROW = {
  AFFIRM: {
    className: DEFAULT_CLASS_NAME,
    displayMessage: AffirmMessage,
  },
  AMAZON: {
    className: DEFAULT_CLASS_NAME,
    displayMessage: AmazonMessage,
  },
  BEST_BUY: {
    className: DEFAULT_CLASS_NAME,
    displayMessage: BestBuyMessage,
  },
  EXPEDITED_SHIPPING: {
    className: 'bg-gray-600',
    displayMessage: ExpeditedShippingMessage,
  },
  FSA: {
    className: DEFAULT_CLASS_NAME,
    displayMessage: FSAMessage,
  },
  JAPAN: {
    className: 'bg-sandstone-500 text-sandstone-200',
    displayMessage: JapanMessage,
  },
  NATURAL_CYCLES: {
    className: DEFAULT_CLASS_NAME,
    displayMessage: NaturalCyclesMessage,
  },
  // Display the hidden afilliate eyebrow by default
  DEFAULT: {
    displayMessage: AffiliateMessage,
    className: 'hidden',
    id: 'partner_eyebrow',
  },
};

// eslint-disable-next-line complexity
const useEyebrowProps = (pathname: string): EyebrowProps => {
  // this test is running only in U.S.
  const isAffirmPromoEnabled = useFeatureFlag('affirm-homepage-promo');
  const isJapanPromoEnabled = useFeatureFlag('enable-japan-eyebrow');
  const isExpeditedShippingPromoEnabled = useFeatureFlag(
    'enable-expedited-shipping-banner',
  );
  const isNaturalCyclesEyebrowEnabled = useGate('natural_cycles_eyebrow').value;
  const showBestBuy = useFeatureFlag('show-best-buy');
  const isBestBuyEyebrowEnabled = useGate('best_buy_eyebrow').value;
  const isAffirmCountry = useDisplayAffirm();
  const isJapan = useMatchUserCountry('JP');
  const isFsaEyebrowEnabled = useFeatureFlag('enable-fsa-eyebrow');
  const { isEnabled: isSikaEnabled } = useSikaPayments();
  const { config } = useConfig('eyebrow');
  const activeEyebrowConfig = Object.keys(config.value).length > 0;

  // organize by pathname and precedence
  if (pathname === '/') {
    if (activeEyebrowConfig) {
      return {
        className: DEFAULT_CLASS_NAME,
        displayMessage: () => (
          <GenericEyebrow
            {...(config.value as unknown as GenericEyebrowProps)}
          />
        ),
      };
    }

    if (isFsaEyebrowEnabled && isSikaEnabled) {
      return EYEBROW.FSA;
    }

    if (isJapanPromoEnabled && isJapan) {
      return EYEBROW.JAPAN;
    }

    if (isNaturalCyclesEyebrowEnabled) {
      return EYEBROW.NATURAL_CYCLES;
    }

    if (isBestBuyEyebrowEnabled && showBestBuy) {
      return EYEBROW.BEST_BUY;
    }

    if (isAffirmPromoEnabled && isAffirmCountry) {
      return EYEBROW.AFFIRM;
    }

    if (isExpeditedShippingPromoEnabled) {
      return EYEBROW.EXPEDITED_SHIPPING;
    }
  }

  if (pathname === '/product/rings') {
    return EYEBROW.DEFAULT;
  }

  return { className: 'hidden', displayMessage: () => <></> };
};

export default useEyebrowProps;
